import React, { useEffect, useRef } from 'react';
import { Plus, Edit, Trash, Download, Bell, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useServices } from '../hooks/useServices';

const ServicesSection = ({ themes, currentTheme, accounts }) => {
  const {
    services,
    loading,
    error,
    showServiceForm,
    setShowServiceForm,
    showTemplateForm,
    setShowTemplateForm,
    notificationTemplateText,
    setNotificationTemplateText,
    newService,
    setNewService,
    currentServicePage,
    itemsPerPage,
    fetchServices,
    handleSaveService,
    handleDeleteService,
    handleEditService,
    handleImageUpload,
    handleTemplateEdit,
    handleSaveTemplate,
    paginationInfo,
    handleNextPage,
    handlePrevPage,
    handleItemsPerPageChange,
    setError
  } = useServices();

  const fileInputRef = useRef();

  // Cargar servicios cuando se monta la sección (independiente del Dashboard)
  useEffect(() => {
    // Solo cargar si no hay servicios cargados
    if (!services || services.length === 0) {
      fetchServices(1, 50); // Cargar primera página con 50 elementos
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar una vez al montar

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };


  const safeReplaceTemplateVars = (template, replacements) => {
    if (!template) return '';
    
    return template.replace(/\{\{([^{}]+)\}\}/g, (match, key) => {
      return replacements.hasOwnProperty(key) ? replacements[key] || '' : match;
    });
  };

  // Función para obtener todas las variables disponibles para plantillas de servicios
  const getServiceTemplateVariables = () => {
    return [
      '{{nombre_cliente}}',
      '{{nombre_servicio}}',
      '{{correo}}',
      '{{contraseña}}',
      '{{perfil}}',
      '{{pin}}',
      '{{fecha_vencimiento}}',
      '{{observaciones}}',  // NUEVA VARIABLE AGREGADA
      '{{precio}}',
      '{{telefono}}',
      '{{dias_restantes}}',
      '{{dias_expirado}}',
      '{{nueva_contraseña}}',
      '{{tipo_problema}}',
      '{{fecha_reporte}}'
    ];
  };

  // Función para generar datos de ejemplo seguros para la vista previa
  const generatePreviewData = () => {
    // Obtener un nombre de servicio de manera segura
    let serviceName = 'Netflix Premium';
    if (services && services.length > 0) {
      serviceName = services[0].name || 'Servicio de Ejemplo';
    }

    return {
      nombre_cliente: 'Juan Pérez',
      nombre_servicio: serviceName,
      correo: 'ejemplo@correo.com',
      contraseña: '••••••',
      perfil: 'Perfil 1',
      pin: '12345',
      fecha_vencimiento: new Date().toLocaleDateString(),
      observaciones: 'Cuenta premium con acceso completo',
      precio: '$15.000',
      telefono: '+57 300 123 4567',
      dias_restantes: '5',
      dias_expirado: '0',
      nueva_contraseña: 'NuevaPass123',
      tipo_problema: 'Falla de conexión',
      fecha_reporte: new Date().toLocaleDateString()
    };
  };

  if (loading && services.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin mx-auto text-blue-500" />
          <p className={`mt-2 ${themes[currentTheme].textMuted}`}>Cargando servicios...</p>
        </div>
      </div>
    );
  }

  if (error && services.length === 0) {
    return (
      <div className="p-6 bg-red-900 bg-opacity-25 rounded-lg">
        <p className="text-red-500">{error}</p>
        <button 
          className="mt-2 px-4 py-2 bg-blue-600 rounded-md text-white"
          onClick={() => {
            setError(null);
            fetchServices();
          }}
        >
          Intentar de nuevo
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className={`text-2xl font-bold ${themes[currentTheme].textPrimary}`}>Listado de Servicios</h1>
        <button 
          className="bg-green-500 text-white px-4 py-2 rounded-md flex items-center gap-2"
          onClick={() => {
            setNewService({ name: '', url: '', price: 0, maxProfiles: 0, image: '' });
            setShowServiceForm(true);
          }}
        >
          <Plus size={20} /> Nuevo
        </button>
      </div>
      
      {services.length === 0 ? (
        <div className={`${themes[currentTheme].bgSecondary} p-8 rounded-lg text-center`}>
          <p className={themes[currentTheme].textMuted}>No hay servicios registrados.</p>
          <button 
            className="mt-4 px-4 py-2 bg-blue-600 rounded-md text-white"
            onClick={() => {
              setNewService({ name: '', url: '', price: 0, maxProfiles: 0, image: '' });
              setShowServiceForm(true);
            }}
          >
            Crear el primer servicio
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className={`${themes[currentTheme].bgSecondary} rounded-lg overflow-hidden`}>
              <table className="min-w-full divide-y divide-gray-800">
                <thead className={themes[currentTheme].bgPrimary}>
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>#</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Servicio</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Link producto</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Precio</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Max Perfiles</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}># Cuentas</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Acciones</th>
                  </tr>
                </thead>
                <tbody className={`${themes[currentTheme].bgSecondary} divide-y divide-gray-800`}>
                  {services.map((service, index) => {
                    // Usa el conteo precargado en lugar de calcularlo
                    const serviceAccounts = service.accounts_count || 0;
                    
                    const displayIndex = (currentServicePage - 1) * itemsPerPage + index + 1;
                    
                    return (
                      <tr key={service.id}>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>{displayIndex}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <img className="h-10 w-10 rounded-full" src={service.image} alt={service.name} />
                            </div>
                            <div className="ml-4">
                              <div className={`text-sm font-medium ${themes[currentTheme].textPrimary}`}>{service.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-400">{service.url}</td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>{formatCurrency(service.price)}</td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>{service.max_profiles || service.maxProfiles}</td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>{serviceAccounts}</td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>
                          <div className="flex space-x-2">
                            <button 
                              className="text-blue-400 hover:text-blue-300"
                              onClick={() => handleTemplateEdit(service.id)}
                            >
                              <Bell size={16} />
                            </button>
                            <button 
                              className="text-green-400 hover:text-green-300"
                              onClick={() => alert(`Descargando información de ${service.name}`)}
                            >
                              <Download size={16} />
                            </button>
                            <button 
                              className="text-blue-400 hover:text-blue-300"
                              onClick={() => handleEditService(service)}
                            >
                              <Edit size={16} />
                            </button>
                            <button 
                              className="text-red-400 hover:text-red-300"
                              onClick={() => handleDeleteService(service.id)}
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center">
          <span className={`mr-2 text-sm ${themes[currentTheme].textMuted}`}>Filas por página:</span>
          <select 
            className={`${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary} rounded-md px-2 py-1 text-sm`}
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </select>
        </div>
        <div className="flex items-center">
          <span className={`mr-4 text-sm ${themes[currentTheme].textMuted}`}>
            {paginationInfo.totalCount > 0 
              ? `${(paginationInfo.currentPage - 1) * itemsPerPage + 1}-${Math.min(paginationInfo.currentPage * itemsPerPage, paginationInfo.totalCount)} de ${paginationInfo.totalCount}` 
              : '0-0 de 0'}
          </span>
          <div className="flex space-x-1">
            <button 
              className={`p-1 rounded-md hover:${themes[currentTheme].bgTertiary} ${!paginationInfo.hasPrevPage ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={handlePrevPage}
              disabled={!paginationInfo.hasPrevPage}
            >
              <ChevronLeft size={20} className={themes[currentTheme].textMuted} />
            </button>
            <button 
              className={`p-1 rounded-md hover:${themes[currentTheme].bgTertiary} ${!paginationInfo.hasNextPage ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={handleNextPage}
              disabled={!paginationInfo.hasNextPage}
            >
              <ChevronRight size={20} className={themes[currentTheme].textMuted} />
            </button>
          </div>
        </div>
      </div>
      
      {showServiceForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${themes[currentTheme].bgSecondary} p-6 rounded-lg w-full max-w-md`}>
            <h2 className={`text-xl font-bold mb-4 ${themes[currentTheme].textPrimary}`}>
              {newService.id ? 'Editar Servicio' : 'Nuevo Servicio'}
            </h2>
            <form className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Nombre del Servicio</label>
                <input 
                  type="text" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={newService.name}
                  onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>URL del Servicio</label>
                <input 
                  type="url" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={newService.url}
                  onChange={(e) => setNewService({ ...newService, url: e.target.value })}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Precio</label>
                <input 
                  type="number" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`} 
                  placeholder="0"
                  value={newService.price}
                  onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Número Máximo de Perfiles</label>
                <input 
                  type="number" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={newService.maxProfiles}
                  onChange={(e) => setNewService({ ...newService, maxProfiles: e.target.value })}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Imagen</label>
                <div className="flex items-center gap-2">
                  <div className={`w-16 h-16 ${themes[currentTheme].bgTertiary} rounded-md flex items-center justify-center`}>
                    {newService.image ? (
                      <img src={newService.image} alt="Preview" className="h-full w-full object-cover rounded-md" />
                    ) : (
                      <Plus size={24} className={themes[currentTheme].textMuted} />
                    )}
                  </div>
                  <div>
                    <input 
                      type="file" 
                      ref={fileInputRef} 
                      onChange={handleImageUpload} 
                      className="hidden" 
                      accept="image/*" 
                    />
                    <button 
                      type="button" 
                      className="px-3 py-1 bg-blue-600 rounded text-sm text-white"
                      onClick={() => fileInputRef.current.click()}
                    >
                      Seleccionar archivo
                    </button>
                    <p className={`text-xs ${themes[currentTheme].textMuted} mt-1`}>PNG, JPG o SVG (Max. 800x800px)</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button 
                  type="button" 
                  className={`px-4 py-2 border ${themes[currentTheme].borderColor} rounded-md ${themes[currentTheme].textSecondary}`}
                  onClick={() => {
                    setShowServiceForm(false);
                    if (newService.id) {
                      setNewService({ name: '', url: '', price: 0, maxProfiles: 0, image: '' });
                    }
                  }}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="px-4 py-2 bg-blue-600 rounded-md text-white"
                  onClick={handleSaveService}
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal actualizado para personalizar la plantilla de notificación */}
      {showTemplateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${themes[currentTheme].bgSecondary} p-6 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-white">Editar Plantilla de Notificación</h2>
              <button 
                className="text-gray-400 hover:text-white"
                onClick={() => setShowTemplateForm(false)}
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Panel izquierdo: Variables disponibles */}
              <div className={`${themes[currentTheme].bgTertiary} p-4 rounded-lg`}>
                <h3 className="text-lg font-semibold mb-4 text-white">Variables Disponibles</h3>
                <p className="text-sm text-gray-300 mb-4">
                  Haz clic en cualquier variable para insertarla en la plantilla:
                </p>
                <div className="space-y-1">
                  {getServiceTemplateVariables().map(variable => (
                    <div
                      key={variable}
                      className={`text-xs px-2 py-1 ${themes[currentTheme].bgSecondary} rounded font-mono cursor-pointer hover:bg-opacity-70 text-blue-300 hover:text-blue-200 transition-colors`}
                      onClick={() => {
                        const textarea = document.getElementById('service-template-textarea');
                        if (textarea) {
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const text = notificationTemplateText;
                          const newText = text.substring(0, start) + variable + text.substring(end);
                          setNotificationTemplateText(newText);
                          // Re-focus and set cursor position
                          setTimeout(() => {
                            textarea.focus();
                            textarea.setSelectionRange(start + variable.length, start + variable.length);
                          }, 0);
                        }
                      }}
                      title="Clic para insertar en la plantilla"
                    >
                      {variable}
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 p-3 bg-blue-900 bg-opacity-30 rounded-md">
                  <h4 className="text-sm font-semibold text-blue-300 mb-2">💡 Descripción de Variables:</h4>
                  <div className="text-xs text-blue-200 space-y-1">
                    <div><strong>{'{{nombre_cliente}}'}</strong>: Nombre del cliente</div>
                    <div><strong>{'{{observaciones}}'}</strong>: Observaciones de la cuenta</div>
                    <div><strong>{'{{correo}}'}</strong>: Email/usuario de la cuenta</div>
                    <div><strong>{'{{contraseña}}'}</strong>: Contraseña de acceso</div>
                    <div><strong>{'{{perfil}}'}</strong>: Nombre del perfil asignado</div>
                    <div><strong>{'{{pin}}'}</strong>: PIN del perfil</div>
                  </div>
                </div>
              </div>

              {/* Panel central: Editor de plantilla */}
              <div className={`${themes[currentTheme].bgTertiary} p-4 rounded-lg`}>
                <h3 className="text-lg font-semibold mb-4 text-white">Editor de Plantilla</h3>
                <textarea
                  id="service-template-textarea"
                  className={`w-full h-80 p-3 rounded-md ${themes[currentTheme].bgSecondary} border ${themes[currentTheme].borderColor} text-white font-mono text-sm resize-none`}
                  value={notificationTemplateText}
                  onChange={e => setNotificationTemplateText(e.target.value)}
                  placeholder="Escribe tu plantilla aquí. Usa las variables del panel izquierdo..."
                />
                
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
                    onClick={() => setShowTemplateForm(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
                    onClick={handleSaveTemplate}
                  >
                    Guardar Plantilla
                  </button>
                </div>
              </div>

              {/* Panel derecho: Vista previa */}
              <div className={`${themes[currentTheme].bgTertiary} p-4 rounded-lg`}>
                <h3 className="text-lg font-semibold mb-4 text-white">Vista Previa</h3>
                
                <div className={`${themes[currentTheme].bgSecondary} p-4 rounded-md border-2 border-blue-600 min-h-80`}>
                  <div className="text-white whitespace-pre-wrap text-sm leading-relaxed">
                    {safeReplaceTemplateVars(notificationTemplateText, generatePreviewData())}
                  </div>
                </div>

                <div className="mt-4 text-xs text-gray-400">
                  <p className="mb-2">💡 <strong>Consejos:</strong></p>
                  <ul className="space-y-1 ml-4">
                    <li>• Usa emojis para hacer el mensaje más atractivo</li>
                    <li>• Mantén un tono profesional pero amigable</li>
                    <li>• Incluye información clara y concisa</li>
                    <li>• Haz clic en las variables para insertarlas</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* File input escondido para subir imágenes */}
      <input 
        type="file" 
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        onChange={handleImageUpload}
      />
    </div>
  );
};

export default ServicesSection;
